import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { HorizontalScrollFade } from './horizontal-scroll-fade';

import { cn } from '@/lib/utils';

import { PAGE_TABS_APPEARANCE } from './types/page-tabs-variants-enums';

interface PageTabsContextValue {
  appearance: PAGE_TABS_APPEARANCE;
}

const PageTabsContext = React.createContext<PageTabsContextValue | null>(null);

function usePageTabsContext(partName: string): PageTabsContextValue {
  const context = React.use(PageTabsContext);

  if (!context) {
    throw new Error(`${partName} must be used within PageTabs.Root`);
  }

  return context;
}

export interface PageTabsRootProps
  extends Omit<React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>, 'orientation'> {
  appearance?: PAGE_TABS_APPEARANCE;
  ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.Root>>;
}

function PageTabsRoot({
  appearance = PAGE_TABS_APPEARANCE.UNDERLINE,
  children,
  className,
  ref,
  ...props
}: PageTabsRootProps) {
  const isVertical = appearance === PAGE_TABS_APPEARANCE.VERTICAL_NAVIGATION;
  const contextValue: PageTabsContextValue = { appearance };

  return (
    <PageTabsContext value={contextValue}>
      <TabsPrimitive.Root
        {...props}
        ref={ref}
        orientation={isVertical ? 'vertical' : 'horizontal'}
        data-slot="page-tabs-root"
        data-appearance={appearance}
        className={cn(
          'flex min-w-0 text-content-secondary',
          isVertical ? 'items-start gap-4' : 'flex-col',
          className
        )}
      >
        {children}
      </TabsPrimitive.Root>
    </PageTabsContext>
  );
}

export interface PageTabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.List>>;
}

function PageTabsList({ className, ref, ...props }: PageTabsListProps) {
  const { appearance } = usePageTabsContext('PageTabs.List');
  const isVertical = appearance === PAGE_TABS_APPEARANCE.VERTICAL_NAVIGATION;

  if (isVertical) {
    return (
      <TabsPrimitive.List
        {...props}
        ref={ref}
        data-slot="page-tabs-list"
        className={cn(
          'flex min-w-0 w-56 shrink-0 flex-col border-r border-border-subtle pr-2',
          className
        )}
      />
    );
  }

  return (
    <HorizontalScrollFade
      className="w-full border-b border-border-subtle"
      fadeFromClassName="from-surface-canvas"
      viewportClassName="w-full"
    >
      <TabsPrimitive.List
        {...props}
        ref={ref}
        data-slot="page-tabs-list"
        className={cn('flex w-max min-w-full', className)}
      />
    </HorizontalScrollFade>
  );
}

export interface PageTabsTabProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.Trigger>>;
}

function PageTabsTab({ className, ref, ...props }: PageTabsTabProps) {
  const { appearance } = usePageTabsContext('PageTabs.Tab');
  const isVertical = appearance === PAGE_TABS_APPEARANCE.VERTICAL_NAVIGATION;

  return (
    <TabsPrimitive.Trigger
      {...props}
      ref={ref}
      data-slot="page-tabs-tab"
      className={cn(
        'relative inline-flex h-10 shrink-0 items-center whitespace-nowrap rounded-control px-3 type-nav font-medium text-content-secondary ring-offset-background transition-[color,background-color,border-color,box-shadow] duration-fast ease-standard',
        'pointer-fine:hover:bg-neutral-interaction-hover pointer-fine:hover:text-content-primary active:bg-neutral-interaction-pressed',
        'focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:bg-transparent disabled:text-content-disabled disabled:opacity-100',
        'data-[state=active]:font-semibold data-[state=active]:text-content-primary',
        'motion-reduce:transition-none pointer-coarse:h-11',
        isVertical
          ? 'w-full justify-start border-l-2 border-l-transparent data-[state=active]:border-l-interaction-selected-indicator'
          : '-mb-px justify-center rounded-b-none border-b-2 border-b-transparent data-[state=active]:border-b-interaction-selected-indicator',
        className
      )}
    />
  );
}

export interface PageTabsPanelProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {
  ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.Content>>;
}

function PageTabsPanel({ className, ref, ...props }: PageTabsPanelProps) {
  const { appearance } = usePageTabsContext('PageTabs.Panel');
  const isVertical = appearance === PAGE_TABS_APPEARANCE.VERTICAL_NAVIGATION;

  return (
    <TabsPrimitive.Content
      {...props}
      ref={ref}
      data-slot="page-tabs-panel"
      className={cn(
        'min-w-0 flex-1 text-content-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
        isVertical ? 'w-full' : 'pt-4',
        className
      )}
    />
  );
}

export { PageTabsList, PageTabsPanel, PageTabsRoot, PageTabsTab };
